import React, { useEffect, useState } from 'react';
import { ArrowLeft, Trash2, Save, AlertCircle } from 'lucide-react';
import { useDispatch } from 'react-redux';
import {
  success,
  error as toastError,
  WarningDialog,
} from '@innovyze/stylovyze';
import {
  createContextNote,
  deleteContextNote,
  getContextNote,
  HttpError,
  updateContextNote,
} from '@/services/knowledgeApi';
import type {
  ApplyMode,
  TenantContextNote,
  UserContextNote,
} from '@Types/knowledge';

type EditableNote = UserContextNote | TenantContextNote;

interface NoteEditorProps {
  contextId?: string;
  createScope?: 'user' | 'tenant';
  defaultApplyMode?: ApplyMode;
  readOnly?: boolean;
  onBack: () => void;
  onSaved: (note: EditableNote) => void;
  onDeleted?: () => void;
}

export const NoteEditor = ({
  contextId,
  createScope = 'user',
  defaultApplyMode = 'chat',
  readOnly = false,
  onBack,
  onSaved,
  onDeleted,
}: NoteEditorProps) => {
  const dispatch = useDispatch();
  const isCreate = !contextId;

  const [note, setNote] = useState<EditableNote | null>(null);
  const scope: 'user' | 'tenant' = isCreate
    ? createScope
    : note?.scope ?? createScope;
  const isTenant = scope === 'tenant';
  const [loading, setLoading] = useState(!isCreate);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [summary, setSummary] = useState('');
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [applyMode, setApplyMode] = useState<ApplyMode>(defaultApplyMode);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isCreate) {
      setApplyMode(defaultApplyMode);
    }
  }, [isCreate, defaultApplyMode]);

  useEffect(() => {
    if (!contextId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getContextNote(contextId)
      .then(n => {
        if (cancelled) return;
        setNote(n);
        setName(n.name);
        setBody(n.body || '');
        setSummary(n.summary || n.description || '');
        setSummaryDirty(false);
        setApplyMode(n.apply_mode || 'chat');
      })
      .catch(e => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load note');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextId]);

  const trimmedName = name.trim();
  const trimmedBody = body.trim();
  const canSave = trimmedName !== '' && trimmedBody !== '';

  const handleSave = async () => {
    if (!canSave || saving || readOnly) return;
    setSaving(true);
    try {
      // Only send a summary the user actually typed. Leaving `description` out
      // is what tells the server to regenerate it from the new body, which is
      // what people expect when they have not touched the field.
      const trimmedSummary = summary.trim();
      const summaryPatch =
        summaryDirty && trimmedSummary ? { description: trimmedSummary } : {};

      const saved = isCreate
        ? await createContextNote({
            name: trimmedName,
            body: trimmedBody,
            scope,
            apply_mode: applyMode,
            ...summaryPatch,
          })
        : await updateContextNote(note!.context_id, {
            name: trimmedName,
            body: trimmedBody,
            apply_mode: applyMode,
            ...summaryPatch,
          });
      dispatch(success(isCreate ? 'Note created' : 'Note saved'));
      onSaved(saved as EditableNote);
      onBack();
    } catch (e) {
      const message =
        e instanceof HttpError
          ? e.message
          : e instanceof Error
          ? e.message
          : 'Save failed';
      dispatch(toastError(message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!note) return;
    setDeleting(true);
    try {
      await deleteContextNote(note.context_id);
      dispatch(success(`Deleted "${note.name}"`));
      onDeleted?.();
    } catch (e) {
      dispatch(toastError(e instanceof Error ? e.message : 'Delete failed'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#3C3C3C] border-t-transparent" />
      </div>
    );
  }

  if (loadError || (!isCreate && !note)) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-[#252F33]">{loadError || 'Not found'}</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-3 text-sm text-[#006EAF] hover:underline">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-[#252F33] hover:text-[#006EAF]">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <span className="text-xs text-[#768D95]">
          {isCreate
            ? 'New note'
            : readOnly
            ? 'Read-only'
            : `v${note?.version ?? ''}`}
        </span>
      </div>

      <div className="space-y-4 flex-1 overflow-y-auto pr-1">
        {!isCreate && note && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3">
            <Meta label="Author" value={note.author_display_name || '—'} />
            <Meta
              label="Created"
              value={
                note.created_at
                  ? new Date(note.created_at).toLocaleDateString()
                  : '—'
              }
            />
            <Meta
              label="Prompt used"
              value={note.source_prompt?.trim() || '—'}
              full
            />
          </div>
        )}

        <div>
          <label
            htmlFor="note-name"
            className="block text-xs font-semibold mb-1">
            Name
          </label>
          <input
            id="note-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            readOnly={readOnly}
            disabled={readOnly}
            maxLength={100}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007CA0] disabled:bg-slate-50"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label
              htmlFor="note-summary"
              className="block text-xs font-semibold">
              Summary
            </label>
            {!readOnly && summaryDirty && (
              <button
                type="button"
                onClick={() => {
                  setSummary('');
                  setSummaryDirty(false);
                }}
                className="text-xs text-[#007CA0] hover:underline">
                Regenerate from body
              </button>
            )}
          </div>
          <textarea
            id="note-summary"
            value={summary}
            onChange={e => {
              setSummary(e.target.value);
              setSummaryDirty(true);
            }}
            readOnly={readOnly}
            disabled={readOnly}
            rows={2}
            maxLength={1000}
            placeholder="Left blank, a summary is generated from the note body when you save."
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007CA0] disabled:bg-slate-50"
          />
        </div>

        <p className="text-xs text-[#768D95]">
          {summaryDirty
            ? 'Your summary will be saved exactly as written. Use “Regenerate from body” to go back to an automatic one.'
            : isCreate
            ? 'The summary appears in your library, and in Memory when set to always-on.'
            : 'The summary is regenerated from the note body on save, unless you edit it.'}
        </p>

        <div>
          <label
            htmlFor="note-body"
            className="block text-xs font-semibold mb-1">
            Note body
          </label>
          <textarea
            id="note-body"
            value={body}
            onChange={e => setBody(e.target.value)}
            readOnly={readOnly}
            disabled={readOnly}
            maxLength={8192}
            rows={10}
            placeholder="Thresholds, definitions, commitments..."
            className="w-full px-3 py-2 text-sm font-mono border border-slate-200 rounded-md resize-y disabled:bg-slate-50"
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold text-[#252F33]">
            When should Bernoulli use this?
          </legend>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="apply_mode"
              checked={applyMode === 'chat'}
              onChange={() => setApplyMode('chat')}
              disabled={readOnly}
            />
            <span>
              <strong>This chat only</strong> — apply via the Knowledge picker
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="apply_mode"
              checked={applyMode === 'always'}
              onChange={() => setApplyMode('always')}
              disabled={readOnly}
            />
            <span>
              <strong>Always considered</strong> — appears in Memory and is
              injected when relevant (you do not need to attach it in chat)
            </span>
          </label>
        </fieldset>
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-200">
          {!isCreate ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline">
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md bg-[#007CA0] text-white disabled:opacity-50">
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      {confirmDelete && note && (
        <WarningDialog
          open={confirmDelete}
          title="Delete note?"
          cancelText="Cancel"
          confirmText={deleting ? 'Deleting...' : 'Delete'}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleDelete}>
          <p className="text-sm text-[#252F33]">
            {`"${note.name}" will be deleted. This cannot be undone.`}
          </p>
        </WarningDialog>
      )}
    </div>
  );
};

const Meta = ({
  label,
  value,
  full,
}: {
  label: string;
  value: string;
  full?: boolean;
}) => (
  <div className={full ? 'sm:col-span-2' : undefined}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#768D95]">
      {label}
    </p>
    <p className="text-sm text-[#252F33] mt-1 leading-relaxed">{value}</p>
  </div>
);
